import { useCallback, useEffect, useRef, useState } from "react";
import { setAuthFailureHandler } from "../services/api";
import { tokenStore, clearLegacyStorage, withoutBearer } from "../auth/tokenStore";
import { AuthError, exchangeRefreshToken, fetchSessionUser, readUrlAuth } from "../auth/vioSession";
import { getParentOrigin, isEmbedded } from "../utils/parentOrigin";

// Without URL tokens, an embedding host may still post VIO_AUTH_TOKENS.
const HOST_TOKEN_WAIT_MS = 3000;

/**
 * Who is playing, from VIO only.
 *   - tokens on the iframe URL (Member App page: refreshToken; home section: token)
 *   - VIO_AUTH_TOKENS from the host (on load, on request, on token change)
 * Opened without tokens (standalone / "open in new tab") -> authProblem
 * "signin"; a previous member's session is never reused.
 *
 * Returns { user, isAuthenticated, authProblem, setAuthProblem, authLoading }.
 * authProblem: null | "signin" | "legal" (terms pending in VIO).
 */
export default function useVioAuth() {
  const [user, setUser] = useState(null);
  const [authProblem, setAuthProblem] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const userIdRef = useRef(null);

  const applyUser = useCallback((info) => {
    userIdRef.current = info.userId;
    // The host re-sends tokens for the same member: keep the same object so
    // effects keyed on `user` don't reload everything.
    setUser((prev) => (prev?.userId === info.userId ? prev : info));
    setAuthProblem(null);
    setAuthLoading(false);
  }, []);

  const signOut = useCallback((reason) => {
    tokenStore.clearTokens();
    userIdRef.current = null;
    setUser(null);
    setAuthProblem(reason);
    setAuthLoading(false);
  }, []);

  const signInWith = useCallback(async ({ refreshToken, accessToken }) => {
    if (refreshToken) tokenStore.setRefreshToken(refreshToken);
    const access = accessToken || (refreshToken ? await exchangeRefreshToken(refreshToken) : null);
    if (!access) throw new AuthError("signin", "No token");
    const info = await fetchSessionUser(access);
    tokenStore.setAccessToken(access);
    applyUser(info);
    return info;
  }, [applyUser]);

  // A failed sign-in only matters while nobody is signed in (the host message
  // may have succeeded meanwhile).
  const reportFailure = useCallback((error) => {
    if (error?.kind === "legal") {
      setAuthProblem("legal");
      setAuthLoading(false);
      return;
    }
    if (!userIdRef.current) {
      setAuthProblem("signin");
      setAuthLoading(false);
    }
  }, []);

  // Session lost later (refresh rejected) or terms pending on an API call.
  useEffect(() => {
    setAuthFailureHandler((reason) => {
      if (reason === "legal") {
        setAuthProblem("legal");
        return;
      }
      signOut("signin");
    });
    return () => setAuthFailureHandler(null);
  }, [signOut]);

  useEffect(() => {
    clearLegacyStorage();
    const url = readUrlAuth();
    tokenStore.clearTokens();
    if (url.redirectUrl) tokenStore.setRedirectUrl(url.redirectUrl);

    if (url.hasTokens) {
      signInWith(url).catch(reportFailure);
      return undefined;
    }
    if (isEmbedded() && getParentOrigin()) {
      const timer = setTimeout(() => {
        if (!userIdRef.current) reportFailure(new AuthError("signin", "No tokens from host"));
      }, HOST_TOKEN_WAIT_MS);
      return () => clearTimeout(timer);
    }
    reportFailure(new AuthError("signin", "Opened without VIO tokens"));
    return undefined;
  }, [signInWith, reportFailure]);

  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.type !== "VIO_AUTH_TOKENS") return;
      // Only the embedding VIO host may hand us tokens.
      const hostOrigin = getParentOrigin();
      if (!hostOrigin || event.origin !== hostOrigin) return;
      const payload = event.data.payload || {};
      const refreshToken = withoutBearer(payload.refreshToken);
      const accessToken = withoutBearer(payload.accessToken);
      if (!refreshToken && !accessToken) return;
      signInWith({ refreshToken, accessToken }).catch(reportFailure);
    };
    window.addEventListener("message", onMessage);

    const hostOrigin = getParentOrigin();
    const timers = [];
    if (isEmbedded() && hostOrigin) {
      const ask = () => {
        try {
          window.parent.postMessage({ type: "VIO_REQUEST_TOKENS" }, hostOrigin);
        } catch (_) {
          // host gone / blocked: URL tokens still work
        }
      };
      ask();
      timers.push(setTimeout(ask, 400));
    }
    return () => {
      window.removeEventListener("message", onMessage);
      timers.forEach(clearTimeout);
    };
  }, [signInWith, reportFailure]);

  return {
    user,
    isAuthenticated: Boolean(user),
    authProblem,
    setAuthProblem,
    authLoading,
  };
}
